// FoundationGamble - AZ-305 Azure Solutions Architect
// Histoire : Le CEO annonce une migration Azure en 6 mois. Lucas (Cloud Architect) doit concevoir une architecture resiliente.

import type {
  Character,
  ScenarioChapter,
  SequenceItem,
  StoryContent,
  ChatContent,
  CardContent,
  QuizContent,
  TaskContent,
} from './storyLoader';

export interface FoundationGambleScenario {
  id: string;
  title: string;
  subtitle: string;
  certification: string;
  coverImage: ReturnType<typeof require>;
  characters: Character[];
  chapters: ScenarioChapter[];
}

const foundationGambleCharacters: Character[] = [
  { id: 'samir', name: 'Samir', role: 'CEO',              color: '#1E3A5F', avatar: 'samir' },
  { id: 'tarek', name: 'Tarek', role: 'CTO',              color: '#2D6A4F', avatar: 'tarek' },
  { id: 'lucas', name: 'Lucas', role: 'Cloud Architect',  color: '#4338CA', avatar: 'lucas' },
  { id: 'kwame', name: 'Kwame', role: 'CSO',              color: '#7C3AED', avatar: 'kwame' },
  { id: 'james', name: 'James', role: 'Finance Director', color: '#B45309', avatar: 'james' },
];

// ── Chapitre I ─────────────────────────────────────────────────────────────────

const scene1: SequenceItem = {
  type: 'story',
  id: 'fg-scene-1',
  title: 'Scene 1 : Reunion de crise',
  duration: 4,
  content: {
    narrativeText:
      "Salle de conference au 12e etage. Le skyline de la ville se decoupe derriere les baies vitrees.\n\n" +
      "Samir entre le premier, tablette sous le bras.\n\n" +
      "**Samir :** \"Messieurs, soyons directs. Notre infrastructure on-premise nous coute 40 % de plus que le cloud. La direction a tranche : migration Azure complete en 6 mois.\"\n\n" +
      "**Tarek :** \"Six mois... Pour 200+ services critiques ?\"\n\n" +
      "**Samir :** \"Six mois. Non-negociable. Lucas, c'est votre defi.\"\n\n" +
      "**Lucas :** \"Je comprends. Mais l'architecture doit etre resiliente — pas juste rapide. Une migration bacle = 48h de downtime potentiel.\"\n\n" +
      "**Samir :** \"Voila pourquoi je veux votre plan vendredi avec les SLA garantis.\"\n\n" +
      "**Tarek :** \"Multi-region ou single region ?\"\n\n" +
      "**Lucas :** \"Ca depend des workloads critiques et du budget. James, quel est le plafond ?\"\n\n" +
      "**James :** \"Budget approuve : 2.4M EUR annuel. Pas un euro de plus.\"\n\n" +
      "**Lucas :** \"Multi-region pour les services tier-1, single-region avec Availability Zones pour le reste.\"\n\n" +
      "**Kwame :** \"La securite doit etre integree, pas une surcouche. RGPD aussi.\"\n\n" +
      "**Samir :** \"Entendu. Vendredi 14h. Le pari commence.\"",
    characters: ['samir', 'tarek', 'lucas', 'kwame', 'james'],
  } as StoryContent,
};

const card1: SequenceItem = {
  type: 'card',
  id: 'fg-card-1',
  title: 'Concept : Azure Well-Architected Framework',
  duration: 5,
  content: {
    title: 'Azure Well-Architected Framework : les 5 piliers',
    body:
      "## Azure Well-Architected Framework\n\n" +
      "Microsoft propose 5 piliers pour evaluer la qualite d'une architecture cloud.\n\n" +
      "### 1. Fiabilite (Reliability)\n" +
      "- SLA cibles : 99.9% a 99.999%\n" +
      "- Strategies : redondance, failover automatique, circuit breakers\n" +
      "- Outils : Availability Zones, Traffic Manager, Azure Front Door\n\n" +
      "### 2. Securite (Security)\n" +
      "- Zero Trust : ne jamais faire confiance, toujours verifier\n" +
      "- Defense en profondeur : firewall, WAF, NSG, PIM\n" +
      "- Outils : Defender for Cloud, Azure Sentinel\n\n" +
      "### 3. Optimisation des couts (Cost Optimization)\n" +
      "- Pay-as-you-go vs Reserved Instances\n" +
      "- Right-sizing, auto-scaling, lifecycle policies\n" +
      "- Outils : Azure Cost Management, Advisor\n\n" +
      "### 4. Excellence operationnelle (Operational Excellence)\n" +
      "- IaC (Terraform, Bicep), CI/CD, monitoring\n" +
      "- Outils : Azure Monitor, DevOps, Log Analytics\n\n" +
      "### 5. Efficacite des performances (Performance Efficiency)\n" +
      "- Auto-scaling, CDN, caching Redis\n" +
      "- Outils : Azure Cache for Redis, CDN, Application Insights\n\n" +
      "### Scoring\n" +
      "Utilisez aka.ms/architecture-review pour evaluer votre architecture sur chaque pilier.",
  } as CardContent,
};

const chat1: SequenceItem = {
  type: 'chat',
  id: 'fg-chat-1',
  title: 'Discussion CTO : Multi-region vs Single-region',
  duration: 6,
  content: {
    tutorCharacterId: 'tarek',
    messages: [
      {
        id: 'fg-chat-1-q1',
        sender: 'character',
        characterId: 'tarek',
        text: "Pour nos services de paiement (tier-1), quelle strategie de resilience recommandez-vous ?",
        type: 'single-choice',
        options: [
          "Single region avec Availability Zones uniquement",
          "Multi-region active-active avec Azure Front Door",
          "Multi-region active-passive avec Traffic Manager",
          "On-premise avec backup Azure",
        ],
        correctAnswer: "Multi-region active-active avec Azure Front Door",
        explanation:
          "Pour les services de paiement critiques, une architecture multi-region active-active garantit la haute disponibilite meme en cas de panne d'une region entiere. Azure Front Door assure le routage intelligent et le failover en moins de 60 secondes.",
      },
      {
        id: 'fg-chat-1-q2',
        sender: 'character',
        characterId: 'tarek',
        text: "Quelle est la difference en downtime annuel entre un SLA de 99.9% et 99.99% ?",
        type: 'single-choice',
        options: [
          "99.9% = 8.7h/an | 99.99% = 52 min/an",
          "99.9% = 1h/an | 99.99% = 6 min/an",
          "99.9% = 24h/an | 99.99% = 2.4h/an",
          "Pas de difference significative en pratique",
        ],
        correctAnswer: "99.9% = 8.7h/an | 99.99% = 52 min/an",
        explanation:
          "99.9% (three nines) = 8h45 de downtime autorise par an. 99.99% (four nines) = 52 minutes par an. Pour un service de paiement, 8h de downtime peut representer des millions d'euros de pertes.",
      },
      {
        id: 'fg-chat-1-q3',
        sender: 'character',
        characterId: 'tarek',
        text: "Quel service Azure route le trafic vers la region la plus performante et assure un failover automatique en cas de panne ?",
        type: 'single-choice',
        options: [
          "Azure Load Balancer",
          "Azure Application Gateway",
          "Azure Front Door",
          "Azure Traffic Manager",
        ],
        correctAnswer: "Azure Front Door",
        explanation:
          "Azure Front Door est un CDN global + load balancer L7 + WAF. Il route vers la region la plus proche/rapide et bascule automatiquement en cas de panne. Traffic Manager est DNS-based (L4) et plus lent au failover.",
      },
    ],
  } as ChatContent,
};

const task1: SequenceItem = {
  type: 'task',
  id: 'fg-task-1',
  title: "Concevoir le diagramme d'architecture haute disponibilite",
  duration: 25,
  content: {
    platform: 'Azure Architecture Center / Draw.io',
    objective: "Concevoir une architecture Azure multi-region HA pour les services tier-1",
    steps: [
      {
        stepNumber: 1,
        action: 'Identifier les workloads critiques',
        detail: "Listez les services tier-1 (paiement, auth, API) et tier-2 (reporting, analytics). Definissez les SLA cibles pour chaque tier.",
      },
      {
        stepNumber: 2,
        action: 'Choisir les regions Azure',
        detail: "Tier-1 : West Europe + North Europe en active-active. Tier-2 : West Europe uniquement avec Availability Zones.",
      },
      {
        stepNumber: 3,
        action: 'Definir les SLA par composant',
        detail: "API principale : 99.99% | Paiement : 99.999% | Reporting : 99.5%. Documenter RPO et RTO.",
      },
      {
        stepNumber: 4,
        action: 'Documenter le diagramme',
        detail: "Schema : Azure Front Door > [West Europe: AKS + SQL MI] + [North Europe: AKS + SQL MI]. Geo-replication SQL entre regions.",
      },
    ],
    validation: "Diagramme approuve par Tarek. SLA documentes pour les 3 tiers.",
  } as TaskContent,
};

const quiz1: SequenceItem = {
  type: 'quiz',
  id: 'fg-quiz-1',
  title: 'Quiz : Concepts Azure HA',
  duration: 5,
  content: {
    questions: [
      {
        id: 'fg-q1-1',
        question: "Quel SLA Azure garantit 99.99% pour une app web multi-region ?",
        type: 'single-choice',
        options: [
          "Azure App Service seul",
          "Azure Front Door + 2 App Services dans 2 regions",
          "Azure Load Balancer + 1 App Service",
          "Azure Traffic Manager seul",
        ],
        correctAnswer: "Azure Front Door + 2 App Services dans 2 regions",
        explanation: "Azure Front Door + 2 App Services en multi-region atteint 99.99%. Un App Service seul garantit 99.95%.",
      },
      {
        id: 'fg-q1-2',
        question: "Qu'est-ce qu'Azure Front Door apporte de plus que Traffic Manager ?",
        type: 'multiple-choice',
        options: [
          "Routage L7 (HTTP/HTTPS) vs L4 DNS-based",
          "Caching et acceleration du contenu",
          "Protection WAF integree",
          "Gestion des bases de donnees",
        ],
        correctAnswer: ["Routage L7 (HTTP/HTTPS) vs L4 DNS-based", "Caching et acceleration du contenu", "Protection WAF integree"],
        explanation: "Azure Front Door est un CDN global avec routage L7, caching, WAF et failover rapide. Traffic Manager est DNS-based sans WAF ni caching.",
      },
      {
        id: 'fg-q1-3',
        question: "Dans le Well-Architected Framework, quel pilier couvre le right-sizing et les Reserved Instances ?",
        type: 'single-choice',
        options: ['Fiabilite', 'Securite', 'Optimisation des couts', 'Excellence operationnelle'],
        correctAnswer: 'Optimisation des couts',
        explanation: "Le pilier 'Cost Optimization' du WAF couvre le right-sizing, les Reserved Instances, l'auto-scaling et l'identification des ressources inutilisees avec Azure Advisor.",
      },
    ],
  } as QuizContent,
};

const scene2: SequenceItem = {
  type: 'story',
  id: 'fg-scene-2',
  title: 'Scene 2 : Securite vs Velocite',
  duration: 4,
  content: {
    narrativeText:
      "Vendredi 11h. Lucas presente son architecture a Kwame devant un tableau blanc.\n\n" +
      "**Lucas :** \"Multi-region active-active pour le tier-1, Availability Zones pour le reste. Azure Front Door avec WAF en facade.\"\n\n" +
      "**Kwame :** \"Bien. Et votre Zero Trust ? Je vois pas de microsegmentation.\"\n\n" +
      "**Lucas :** \"On a les NSG par subnet.\"\n\n" +
      "**Kwame :** \"NSG c'est de la securite perimetre. Zero Trust signifie que meme un service interne doit prouver son identite. Un container compromis ne doit pas pouvoir lateraliser.\"\n\n" +
      "**Lucas :** \"Donc Defender for Containers en plus du WAF ?\"\n\n" +
      "**Kwame :** \"Et Azure Policy pour enforcer les configs. Et PIM pour les acces admin. Ca rallonge le planning ?\"\n\n" +
      "**Lucas :** \"Si je l'integre dans le design initial, non. Securite 'built-in', pas 'bolted-on'.\"\n\n" +
      "**Kwame :** \"Vous avez compris. On valide.\"\n\n" +
      "**Lucas :** \"Derniere chose : les donnees clients EU doivent rester en EU pour le RGPD.\"\n\n" +
      "**Kwame :** \"Listez les services non-RGPD. On les exclut du scope. La conformite n'est pas optionnelle.\"",
    characters: ['lucas', 'kwame'],
  } as StoryContent,
};

const card2: SequenceItem = {
  type: 'card',
  id: 'fg-card-2',
  title: 'Concept : Zero Trust Architecture',
  duration: 4,
  content: {
    title: 'Zero Trust : Ne jamais faire confiance, toujours verifier',
    body:
      "## Zero Trust dans Azure\n\n" +
      "### Principes fondamentaux\n" +
      "1. **Verify explicitly** : Authentifier sur tous les signaux disponibles\n" +
      "2. **Use least privilege** : Acces JIT, acces minimal requis\n" +
      "3. **Assume breach** : Minimiser le rayon d'explosion, chiffrer les communications\n\n" +
      "### Composants Zero Trust Azure\n\n" +
      "| Couche | Service Azure |\n" +
      "|---|---|\n" +
      "| Identite | Entra ID + PIM + Conditional Access |\n" +
      "| Applications | Defender for Cloud Apps |\n" +
      "| Infrastructure | Defender for Servers/Containers |\n" +
      "| Reseau | NSG + Azure Firewall + DDoS Protection |\n\n" +
      "### Microsegmentation\n" +
      "Chaque workload dans son sous-reseau avec NSG strict. Communications via Private Endpoints uniquement.\n\n" +
      "### DevSecOps\n" +
      "Integrez la securite dans le pipeline : scans de vulnerabilites, SAST/DAST, validation des policies avant deploiement.",
  } as CardContent,
};

const task2: SequenceItem = {
  type: 'task',
  id: 'fg-task-2',
  title: 'Configurer Azure Front Door avec WAF',
  duration: 30,
  content: {
    platform: 'Azure Portal',
    objective: "Configurer Azure Front Door Premium avec WAF pour l'application multi-region",
    azurePortalPath: 'portal.azure.com > Create a resource > Azure Front Door and CDN profiles',
    steps: [
      {
        stepNumber: 1,
        action: 'Creer un profil Front Door Premium',
        detail: "Create > Front Door and CDN > Front Door Premium. Resource group = 'fg-rg', Nom = 'fg-frontdoor-prod'.",
      },
      {
        stepNumber: 2,
        action: 'Configurer les origines',
        detail: "Add origin group 'primary-origins'. Ajouter Origin 1 (West Europe) et Origin 2 (North Europe). Health probe HTTPS, interval 30s.",
      },
      {
        stepNumber: 3,
        action: 'Configurer la politique WAF',
        detail: "Security > WAF policies > Create. Mode = Prevention. Activer DefaultRuleSet 2.0. Activer protection DDoS.",
      },
      {
        stepNumber: 4,
        action: 'Associer WAF au profil',
        detail: "Front Door > Security policies > Associate WAF policy. Configurer redirect HTTP > HTTPS.",
      },
      {
        stepNumber: 5,
        action: 'Tester le failover',
        detail: "Arreter App Service West Europe. Verifier basculement vers North Europe < 60 secondes.",
      },
    ],
    validation: "Front Door actif avec WAF en mode Prevention. Failover teste < 60s.",
  } as TaskContent,
};

const chapter1: ScenarioChapter = {
  id: 'chapter-1',
  title: "L'Architecture du Risque",
  subtitle: 'AZ-305 — Concevoir une infrastructure Azure resiliente et securisee',
  learningObjectives: [
    "Appliquer le Well-Architected Framework",
    "Concevoir une architecture multi-region haute disponibilite",
    "Comprendre les SLA Azure et leur impact business",
    "Implementer une strategie Zero Trust",
    "Configurer Azure Front Door avec WAF",
  ],
  sequence: [scene1, card1, chat1, task1, quiz1, scene2, card2, task2],
};

export const foundationGambleScenario: FoundationGambleScenario = {
  id: 'foundation-gamble',
  title: 'Foundation Gamble',
  subtitle: "Migration Azure en 6 mois — le pari de l'architecture",
  certification: 'AZ-305',
  coverImage: require('../../../assets/images/characters/nina_raoul_working.png'),
  characters: foundationGambleCharacters,
  chapters: [chapter1],
};

export default foundationGambleScenario;
